import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { useActuals } from '@/hooks/useActuals'
import { useCapital } from '@/hooks/useCapital'
import { useSimulations } from '@/hooks/useSimulations'
import { useBusiness } from '@/hooks/useBusiness'
import { calculateHealthScore } from '@/lib/calculations/dashboardMetrics'
import type { SimulationRun } from '@/lib/db/schema'

type SimWithSummary = SimulationRun & {
  decisionSignal?: string
  endingEquity?: number
  totalRevenue?: number
  npv?: number
}

const fmt = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n)
const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`

const REPORT_TITLES: Record<string, string> = {
  'monthly-performance': 'Laporan Performa Bulanan',
  'investor':            'Investor Report',
  'simulation':          'Simulation Projections',
  'capital-usage':       'Capital & Affordable Loss Report',
}

const SIGNAL_LABELS: Record<string, { label: string; color: string }> = {
  continue:    { label: 'Lanjutkan',     color: '#10b981' },
  improve:     { label: 'Perbaiki',      color: '#f59e0b' },
  add_capital: { label: 'Tambah Modal',  color: '#3b82f6' },
  restructure: { label: 'Restrukturisasi', color: '#8b5cf6' },
  pivot:       { label: 'Pivot',         color: '#f97316' },
  close:       { label: 'Tutup Bisnis',  color: '#ef4444' },
}

export function ReportViewerPage() {
  const { businessId, reportType } = useParams<{ businessId: string; reportType: string }>()
  const { actuals, isLoading: actualsLoading } = useActuals(businessId)
  const { data: capitalData, isLoading: capitalLoading } = useCapital(businessId)
  const { simulations: _simulations, isLoading: simsLoading } = useSimulations(businessId)
  const simulations = _simulations as SimWithSummary[]
  const { business, isLoading: bizLoading } = useBusiness(businessId)

  const isLoading = actualsLoading || capitalLoading || simsLoading || bizLoading
  const settings  = capitalData?.settings ?? null
  const title     = REPORT_TITLES[reportType ?? ''] ?? 'Laporan Bisnis'

  const health    = calculateHealthScore(actuals.length > 0 ? [...actuals].reverse() : [], settings)
  const latestData = actuals.length > 0 ? actuals[0] : null

  const handlePrint = () => window.print()

  if (isLoading) {
    return <div className="p-10 text-center text-slate-500">Memuat laporan...</div>
  }

  const HeaderBar = () => (
    <div className="flex justify-between items-center mb-8 pb-4 border-b border-slate-200 print:hidden">
      <Link to={`/businesses/${businessId}/reports`} className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-medium">
        <ArrowLeft size={16} /> Kembali ke Laporan
      </Link>
      <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors">
        <Printer size={16} /> Cetak / PDF
      </button>
    </div>
  )

  const ReportHeader = () => (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <span className="text-white text-xs font-bold">UC</span>
        </div>
        <span className="text-slate-500 text-sm font-medium">UMKM Business Cockpit</span>
      </div>
      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">{title}</h1>
      {business && <p className="text-slate-600 font-medium mt-1">{business.name}</p>}
      <p className="text-slate-400 text-sm mt-1">Digenerate pada {new Date().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
    </div>
  )

  const ReportFooter = () => (
    <div className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-400 text-center print:mt-auto">
      <p>CONFIDENTIAL — DO NOT DISTRIBUTE Tanpa Izin Manajemen</p>
      <p className="mt-1">UMKM Business Cockpit · {business?.name}</p>
    </div>
  )

  return (
    <div className="bg-white min-h-screen -m-4 sm:-m-6 lg:-m-8 p-8 text-slate-900 print:m-0 print:p-8">
      <div className="max-w-4xl mx-auto print:max-w-full">
        <HeaderBar />
        <ReportHeader />

        {/* ── MONTHLY PERFORMANCE ── */}
        {reportType === 'monthly-performance' && (
          latestData ? (
            <div className="space-y-8">
              {/* Health score banner */}
              <div className="rounded-xl p-5 border" style={{ borderColor: health.score >= 60 ? '#d1fae5' : health.score >= 40 ? '#fef3c7' : '#fee2e2', background: health.score >= 60 ? '#f0fdf4' : health.score >= 40 ? '#fffbeb' : '#fff1f2' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-600">Business Health Score</p>
                    <p className="text-4xl font-black mt-1" style={{ color: health.score >= 60 ? '#059669' : health.score >= 40 ? '#d97706' : '#dc2626' }}>{health.score}<span className="text-lg font-normal text-slate-400">/100</span></p>
                    <p className="text-sm font-medium mt-1 text-slate-600">{health.status}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Periode Terakhir</p>
                    <p className="text-lg font-bold text-slate-800 mt-1">{latestData.periodMonth}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{health.interpretation}</p>
              </div>

              {/* P&L Statement */}
              <div>
                <h2 className="text-xl font-bold mb-4 border-b border-slate-200 pb-2">Laporan Laba Rugi (P&L)</h2>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { label: 'Pendapatan (Revenue)',           value: latestData.revenue,           positive: true },
                      { label: 'HPP / COGS',                     value: -latestData.cogs,              positive: false },
                      { label: 'Laba Kotor (Gross Profit)',      value: latestData.revenue - latestData.cogs, bold: true },
                      { label: 'Biaya Operasional (Opex)',        value: -latestData.operatingExpense, positive: false },
                      { label: 'Laba Operasional',               value: latestData.revenue - latestData.cogs - latestData.operatingExpense, bold: true },
                      { label: 'Pajak',                          value: -latestData.tax,              positive: false },
                      { label: 'Bunga / Interest',               value: -latestData.interest,         positive: false },
                      { label: 'Biaya Lainnya',                  value: -latestData.otherExpense,     positive: false },
                      { label: 'LABA BERSIH (Net Profit)',       value: latestData.revenue - latestData.cogs - latestData.operatingExpense - latestData.tax - latestData.interest - latestData.otherExpense, bold: true, highlight: true },
                    ].map(({ label, value, bold, highlight, positive }) => (
                      <tr key={label} className={highlight ? 'bg-slate-50' : ''}>
                        <td className={`py-2 ${highlight ? 'px-3' : ''} ${bold ? 'font-bold' : ''}`}>{label}</td>
                        <td className={`py-2 text-right ${highlight ? 'px-3' : ''} ${bold ? 'font-bold' : ''} ${value < 0 ? 'text-rose-600' : value > 0 && positive !== false ? 'text-emerald-600' : ''}`}>
                          {value < 0 ? `(${fmt(Math.abs(value))})` : fmt(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cash & Balance */}
              <div>
                <h2 className="text-xl font-bold mb-4 border-b border-slate-200 pb-2">Posisi Kas & Aset</h2>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Kas Akhir', value: latestData.cashBalance },
                    { label: 'Persediaan', value: latestData.inventoryValue },
                    { label: 'Piutang', value: latestData.receivables },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">{label}</p>
                      <p className="text-lg font-bold text-slate-800 mt-1">{fmt(value)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* All periods table */}
              {actuals.length > 1 && (
                <div>
                  <h2 className="text-xl font-bold mb-4 border-b border-slate-200 pb-2">Tren Historis</h2>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-2 px-3 text-slate-600">Periode</th>
                        <th className="text-right py-2 px-3 text-slate-600">Revenue</th>
                        <th className="text-right py-2 px-3 text-slate-600">Gross Profit</th>
                        <th className="text-right py-2 px-3 text-slate-600">Net Profit</th>
                        <th className="text-right py-2 px-3 text-slate-600">Kas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[...actuals].reverse().map(a => {
                        const gp = a.revenue - a.cogs
                        const np = a.revenue - a.cogs - a.operatingExpense - a.tax - a.interest - a.otherExpense
                        return (
                          <tr key={a.id}>
                            <td className="py-2 px-3 font-medium">{a.periodMonth}</td>
                            <td className="py-2 px-3 text-right">{fmt(a.revenue)}</td>
                            <td className={`py-2 px-3 text-right ${gp >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmt(gp)}</td>
                            <td className={`py-2 px-3 text-right font-medium ${np >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmt(np)}</td>
                            <td className="py-2 px-3 text-right">{fmt(a.cashBalance)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 bg-slate-50 rounded-xl text-slate-500 text-center">
              Belum ada data aktual. Tambahkan laporan bulanan aktual terlebih dahulu.
            </div>
          )
        )}

        {/* ── INVESTOR REPORT ── */}
        {reportType === 'investor' && capitalData && (
          <div className="space-y-8">
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
              <h2 className="text-xl font-bold mb-4">Ringkasan Modal</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-slate-500">Modal Awal (Initial Capital)</p>
                  <p className="text-2xl font-black text-slate-800">{fmt(capitalData.settings?.initialCapital ?? 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Modal Dibayar (Paid Capital)</p>
                  <p className="text-2xl font-black text-emerald-700">{fmt(capitalData.metrics?.paidCapital ?? 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Affordable Loss ({capitalData.settings?.affordableLossPercentage ?? 0}%)</p>
                  <p className="text-2xl font-black text-amber-700">{fmt(capitalData.metrics?.affordableLossAmount ?? 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Stop-Loss Equity</p>
                  <p className="text-2xl font-black text-rose-700">{fmt(capitalData.metrics?.stopLossEquity ?? 0)}</p>
                </div>
              </div>
            </div>

            {/* Participants */}
            {capitalData.participants.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 border-b border-slate-200 pb-2">Founder & Investor</h2>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-slate-600">Nama</th>
                      <th className="text-left py-2 px-3 text-slate-600">Peran</th>
                      <th className="text-right py-2 px-3 text-slate-600">Kepemilikan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {capitalData.participants.map(p => (
                      <tr key={p.id}>
                        <td className="py-2 px-3 font-medium">{p.name}</td>
                        <td className="py-2 px-3 text-slate-500 capitalize">{p.role}</td>
                        <td className="py-2 px-3 text-right font-bold">{p.ownershipPercentage?.toFixed(1) ?? 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Contributions */}
            {capitalData.contributions.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 border-b border-slate-200 pb-2">Riwayat Kontribusi Modal</h2>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-slate-600">Tanggal</th>
                      <th className="text-left py-2 px-3 text-slate-600">Jenis</th>
                      <th className="text-left py-2 px-3 text-slate-600">Status</th>
                      <th className="text-right py-2 px-3 text-slate-600">Nominal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {capitalData.contributions.map(c => (
                      <tr key={c.id}>
                        <td className="py-2 px-3">{c.contributionDate ?? '—'}</td>
                        <td className="py-2 px-3 capitalize">{c.type}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${c.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-medium">{fmt(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Health score */}
            {latestData && (
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                <h2 className="text-lg font-bold mb-2">Kondisi Bisnis Saat Ini</h2>
                <div className="flex items-center gap-4">
                  <div className="text-4xl font-black" style={{ color: health.score >= 60 ? '#059669' : health.score >= 40 ? '#d97706' : '#dc2626' }}>
                    {health.score}<span className="text-lg font-normal text-slate-400">/100</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{health.interpretation}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── SIMULATION REPORT ── */}
        {reportType === 'simulation' && (
          simulations.length > 0 ? (
            <div className="space-y-8">
              {simulations.slice(0, 3).map(sim => {
                const signal = SIGNAL_LABELS[sim.decisionSignal ?? 'continue']
                return (
                  <div key={sim.id} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-5 py-3 flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-slate-800">{sim.name}</h3>
                        <p className="text-xs text-slate-500 capitalize">{sim.type} · {sim.durationMonths} bulan</p>
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ color: signal.color, background: `${signal.color}15`, border: `1px solid ${signal.color}30` }}>
                        {signal.label}
                      </span>
                    </div>
                    <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><p className="text-slate-500 text-xs">Initial Capital</p><p className="font-bold mt-0.5">{fmt(sim.initialCapital)}</p></div>
                      <div><p className="text-slate-500 text-xs">Ending Equity</p><p className={`font-bold mt-0.5 ${(sim.endingEquity ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(sim.endingEquity ?? 0)}</p></div>
                      <div><p className="text-slate-500 text-xs">Total Revenue</p><p className="font-bold mt-0.5">{fmt(sim.totalRevenue ?? 0)}</p></div>
                      <div><p className="text-slate-500 text-xs">NPV</p><p className={`font-bold mt-0.5 ${(sim.npv ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(sim.npv ?? 0)}</p></div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="p-8 bg-slate-50 rounded-xl text-slate-500 text-center">
              Belum ada simulasi. Buat simulasi terlebih dahulu.
            </div>
          )
        )}

        {/* ── CAPITAL USAGE ── */}
        {reportType === 'capital-usage' && capitalData && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                <p className="text-sm text-slate-500">Total Committed</p>
                <p className="text-3xl font-black text-slate-800 mt-1">{fmt(capitalData.metrics?.committedCapital ?? 0)}</p>
              </div>
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                <p className="text-sm text-slate-500">Total Paid</p>
                <p className="text-3xl font-black text-emerald-700 mt-1">{fmt(capitalData.metrics?.paidCapital ?? 0)}</p>
              </div>
              <div className="bg-amber-50 p-5 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-700">Affordable Loss Amount</p>
                <p className="text-3xl font-black text-amber-800 mt-1">{fmt(capitalData.metrics?.affordableLossAmount ?? 0)}</p>
                <p className="text-xs text-amber-600 mt-1">{capitalData.settings?.affordableLossPercentage ?? 0}% dari modal awal</p>
              </div>
              <div className="bg-rose-50 p-5 rounded-xl border border-rose-200">
                <p className="text-sm text-rose-700">Stop-Loss Equity Level</p>
                <p className="text-3xl font-black text-rose-800 mt-1">{fmt(capitalData.metrics?.stopLossEquity ?? 0)}</p>
                <p className="text-xs text-rose-600 mt-1">Jika ekuitas jatuh ke angka ini, rekomendasikan untuk menutup bisnis</p>
              </div>
            </div>

            {capitalData.contributions.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 border-b border-slate-200 pb-2">Rincian Penggunaan Modal</h2>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3">Jenis</th>
                      <th className="text-left py-2 px-3">Status</th>
                      <th className="text-left py-2 px-3">Tanggal</th>
                      <th className="text-right py-2 px-3">Nominal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {capitalData.contributions.map(c => (
                      <tr key={c.id}>
                        <td className="py-2 px-3 capitalize font-medium">{c.type}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${c.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-slate-500">{c.contributionDate ?? '—'}</td>
                        <td className="py-2 px-3 text-right font-bold">{fmt(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Fallback */}
        {!['monthly-performance','investor','simulation','capital-usage'].includes(reportType ?? '') && (
          <div className="p-8 bg-slate-50 rounded-xl text-slate-500 text-center">
            Jenis laporan tidak dikenali.
          </div>
        )}

        <ReportFooter />
      </div>
    </div>
  )
}
